import React, { useState, useEffect, useContext, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiX, HiArrowLeft } from 'react-icons/hi';
import useDarkMode from 'utils/hooks/useDarkMode';
import CreateOptionsView from './CreateOptionsView';
import PostTypeSelector from './PostTypeSelector';
import { CreateModalContext } from 'contexts/CreateModalContext';

const AICreativeStudio = lazy(() => import('views/ai_creative_studio'));

const UniversalCreateModal = () => {
    const { isOpen, closeCreateModal } = useContext(CreateModalContext);
    const [isDarkMode] = useDarkMode();
    const [currentView, setCurrentView] = useState('main'); // 'main' | 'postTypeSelector' | 'aiCreativeStudio'

    // Reset view when modal closes
    useEffect(() => {
        if (!isOpen) {
            // Delay reset to allow exit animation to complete
            const timeout = setTimeout(() => {
                setCurrentView('main');
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [isOpen]);

    // Handle ESC key to close modal
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                if (currentView !== 'main') {
                    setCurrentView('main');
                } else {
                    closeCreateModal();
                }
            }
        };

        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [isOpen, currentView, closeCreateModal]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handleSelectPostType = () => {
        setCurrentView('postTypeSelector');
    };

    const handleSelectAICreativeStudio = () => {
        setCurrentView('aiCreativeStudio');
    };

    const handleBackToMain = () => {
        setCurrentView('main');
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence mode="wait">
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-[9999] flex items-center justify-center lg:p-4"
            >
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={closeCreateModal}
                    className="absolute inset-0 bg-black/90 backdrop-blur-xl"
                />

                {/* Modal Content */}
                <motion.div
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ 
                        type: 'spring', 
                        stiffness: 300, 
                        damping: 30,
                        duration: 0.4 
                    }}
                    className="relative w-full max-w-7xl max-h-full overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button (only show on main view) */}
                    {currentView === 'main' && (
                        <motion.button
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={closeCreateModal}
                            className="absolute top-0 right-4 lg:top-0 lg:right-0 z-50 p-3 backdrop-blur-xl rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 shadow-2xl transition-all"
                            aria-label="Close modal"
                        >
                            <HiX className="text-2xl" />
                        </motion.button>
                    )}

                    {/* Content Area with View Transitions */}
                    <AnimatePresence mode="wait">
                        {currentView === 'main' ? (
                            <motion.div
                                key="main"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <CreateOptionsView
                                    onSelectPostType={handleSelectPostType}
                                    onSelectAICreativeStudio={handleSelectAICreativeStudio}
                                    onClose={closeCreateModal}
                                    isDarkMode={isDarkMode}
                                />
                            </motion.div>
                        ) : currentView === 'postTypeSelector' ? (
                            <motion.div
                                key="postTypeSelector"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                <PostTypeSelector
                                    onBack={handleBackToMain}
                                    onClose={closeCreateModal}
                                    isDarkMode={isDarkMode}
                                />
                            </motion.div>
                        ) : (
                            <motion.div
                                key="aiCreativeStudio"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Back & Close buttons */}
                                <div className="flex items-center justify-between mb-3">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleBackToMain}
                                        className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-xl shadow-lg transition-all text-sm font-medium"
                                        aria-label="Back to options"
                                    >
                                        <HiArrowLeft className="text-base" />
                                        Back
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.1, rotate: 90 }}
                                        whileTap={{ scale: 0.9 }}
                                        onClick={closeCreateModal}
                                        className="p-3 backdrop-blur-xl rounded-full bg-white/10 hover:bg-white/20 text-white border border-white/20 shadow-2xl transition-all"
                                        aria-label="Close modal"
                                    >
                                        <HiX className="text-2xl" />
                                    </motion.button>
                                </div>
                                {/* Studio container */}
                                <div className="h-[calc(100vh-6rem)] lg:h-[85vh] rounded-2xl overflow-hidden shadow-2xl">
                                    <Suspense fallback={
                                        <div className="flex items-center justify-center h-full bg-white dark:bg-gray-900 rounded-2xl">
                                            <div className="w-8 h-8 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
                                        </div>
                                    }>
                                        <AICreativeStudio />
                                    </Suspense>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default UniversalCreateModal;

